import { Award, Gift, Loader2, Pencil, Plus, Trash2, Trophy, User } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { compareDivisions } from '../lib/divisionOrder'
import { tournamentsWithoutPrize } from '../lib/groupPrizes'
import { formatWinnerNames, getPrizeWinners, hasPrizeWinner } from '../lib/prizeWinners'
import {
  assignPrizeWinners,
  deletePrize,
  fetchGroupPrizes,
  searchGroupUsers,
  upsertPrize,
} from '../services/groupService'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import GroupBraggingRights from './GroupBraggingRights'
import PrizeWinnersPicker from './PrizeWinnersPicker'
import TypeAheadSearch from './ui/type-ahead-search'

const PRIZE_IDEAS = [
  'Amazon gift card',
  'Free merch from our brand',
  'Lunch at a local restaurant',
  'Front-row parking spot',
]

// Prize form dialog/modal component
function PrizeForm({ prize, brackets, groupId, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    tournament_id: prize?.tournament_id || '',
    bracket_id: prize?.bracket_id || '',
    name: prize?.name || '',
    description: prize?.description || '',
    place: prize?.place || 1,
    promo_code: prize?.promo_code || '',
    redeem_url: prize?.redeem_url || '',
    combined_redeem_url: prize?.combined_redeem_url || '',
  })
  const [saving, setSaving] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState(null)
  const notify = useNotification()

  // Get unique tournaments from brackets (sanctioned by this group)
  const tournaments = brackets.reduce((acc, bracket) => {
    if (!acc.find(t => t.tournament_id === bracket.tournament_id)) {
      acc.push({
        tournament_id: bracket.tournament_id,
        tournament_name: bracket.tournament_name
      })
    }
    return acc
  }, [])

  // Set initial tournament if editing
  useEffect(() => {
    if (prize?.tournament_id && tournaments.length > 0) {
      const tournament = tournaments.find(t => t.tournament_id === prize.tournament_id)
      if (tournament) {
        setSelectedTournament(tournament)
      }
    }
  }, [prize?.tournament_id, brackets])

  // Filter brackets by selected tournament
  const filteredBrackets = formData.tournament_id
    ? brackets.filter(b => b.tournament_id === formData.tournament_id)
    : []

  // Search tournaments
  const searchTournaments = async (query) => {
    const lowerQuery = query.toLowerCase()
    return tournaments.filter(t => 
      t.tournament_name.toLowerCase().includes(lowerQuery)
    ).slice(0, 10)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.tournament_id) {
      notify.error('Please select a tournament')
      return
    }
    if (!formData.name.trim()) {
      notify.error('Please enter a prize name')
      return
    }

    setSaving(true)
    try {
      const data = await upsertPrize({
        id: prize?.id,
        tournamentId: formData.tournament_id,
        bracketId: formData.bracket_id,
        groupId,
        name: formData.name.trim(),
        description: formData.description.trim(),
        place: formData.place,
        promoCode: formData.promo_code.trim(),
        redeemUrl: formData.redeem_url.trim(),
        combinedRedeemUrl: formData.combined_redeem_url.trim(),
      })
      if (!data.success) throw new Error(data.error)

      notify.success(prize ? 'Prize updated!' : 'Prize created!')
      onSave(data.prize)
    } catch (err) {
      console.error('Error saving prize:', err)
      notify.error(err.message || 'Failed to save prize')
    } finally {
      setSaving(false)
    }
  }

  const getPlaceLabel = (place) => {
    switch (place) {
      case 1: return '1st Place'
      case 2: return '2nd Place'
      case 3: return '3rd Place'
      default: return `${place}th Place`
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Tournament *</label>
        <TypeAheadSearch
          value={formData.tournament_id}
          valueLabel={prize?.tournament?.name || selectedTournament?.tournament_name}
          onChange={(tournament) => {
            if (tournament) {
              setSelectedTournament(tournament)
              setFormData({ ...formData, tournament_id: tournament.tournament_id, bracket_id: '' })
            } else {
              setSelectedTournament(null)
              setFormData({ ...formData, tournament_id: '', bracket_id: '' })
            }
          }}
          onSearch={searchTournaments}
          getItemLabel={(t) => t.tournament_name}
          getItemValue={(t) => t.tournament_id}
          placeholder="Search for a tournament..."
          emptyMessage="No tournaments found"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Only tournaments with sanctioned brackets in this group are shown
        </p>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Bracket (Optional)</label>
        <Select value={formData.bracket_id || '__all__'} onValueChange={(val) => setFormData({ ...formData, bracket_id: val === '__all__' ? '' : val })} disabled={!formData.tournament_id}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Tournament-level prize (all brackets)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tournament-level prize (all brackets)</SelectItem>
            {[...filteredBrackets].sort(compareDivisions).map((b) => (
              <SelectItem key={b.bracket_id} value={b.bracket_id}>
                {b.division_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty for tournament-level prize, or select a specific bracket
        </p>
      </div>

      {/* One-tap ideas (owner 2026-10-07) */}
      <div>
        <p className="text-sm font-medium mb-2">Need an idea?</p>
        <div className="flex flex-wrap gap-2">
          {PRIZE_IDEAS.map((idea) => (
            <Button
              key={idea}
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setFormData({ ...formData, name: idea })}
            >
              {idea}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          You deliver this prize to the winner yourself. dub doesn&apos;t provide or send group prizes.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Prize Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="$50 Gift Card"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Place</label>
          <Select value={formData.place.toString()} onValueChange={(val) => setFormData({ ...formData, place: parseInt(val) })}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="1st Place" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((p) => (
                <SelectItem key={p} value={p.toString()}>{getPlaceLabel(p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Description</label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Optional description..."
        />
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Promo Code</label>
        <Input
          value={formData.promo_code}
          onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
          placeholder="SAVE50 or gift card code"
        />
        <p className="text-xs text-muted-foreground mt-1">
          The code the winner will use to redeem
        </p>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Redeem URL</label>
        <Input
          value={formData.redeem_url}
          onChange={(e) => setFormData({ ...formData, redeem_url: e.target.value })}
          placeholder="https://store.example.com"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Where the winner goes to use the code
        </p>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Combined URL (Optional)</label>
        <Input
          value={formData.combined_redeem_url}
          onChange={(e) => setFormData({ ...formData, combined_redeem_url: e.target.value })}
          placeholder="https://store.example.com?code=SAVE50"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Pre-built URL with code embedded (if supported)
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : prize ? 'Update Prize' : 'Create Prize'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

// Winner assignment component. Several winners = a tie (ticket bdec6a1f).
function AssignWinner({ prize, groupId, onAssign, onCancel }) {
  const [winners, setWinners] = useState(() =>
    getPrizeWinners(prize).map((w) => ({ id: w.id, full_name: w.full_name }))
  )
  const [saving, setSaving] = useState(false)
  const notify = useNotification()

  // Search users with group filter
  const searchMembers = useCallback(async (query) => {
    try {
      const data = await searchGroupUsers({
        query,
        groupId,
        limit: 10
      })
      if (!data.success) throw new Error(data.error)

      return data.users || []
    } catch (error) {
      console.error('Error searching users:', error)
      return []
    }
  }, [groupId])

  const handleAssign = async () => {
    setSaving(true)
    try {
      const data = await assignPrizeWinners({
        prizeId: prize.id,
        winnerUserIds: winners.map((w) => w.id),
      })
      if (!data.success) throw new Error(data.error)

      notify.success(
        winners.length === 0 ? 'Winners removed'
          : winners.length === 1 ? 'Winner assigned!'
            : `${winners.length} tied winners assigned!`
      )
      onAssign(data.prize)
    } catch (err) {
      console.error('Error assigning winners:', err)
      notify.error(err.message || 'Failed to assign winners')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Winners</label>
        <PrizeWinnersPicker
          winners={winners}
          onChange={setWinners}
          onSearch={searchMembers}
          disabled={saving}
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={handleAssign} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : 'Save Winners'}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export default function GroupPrizes({ groupId, isOwner, brackets, members }) {
  const [prizes, setPrizes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingPrize, setEditingPrize] = useState(null)
  const [assigningPrize, setAssigningPrize] = useState(null)
  const notify = useNotification()

  useEffect(() => {
    loadPrizes()
  }, [groupId])

  const loadPrizes = async () => {
    try {
      const data = await fetchGroupPrizes({ groupId })
      if (data.success) {
        setPrizes(data.prizes || [])
      }
    } catch (err) {
      console.error('Error loading prizes:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (prizeId) => {
    if (!confirm('Are you sure you want to delete this prize?')) return

    try {
      const data = await deletePrize({ prizeId })
      if (!data.success) throw new Error(data.error)

      notify.success('Prize deleted')
      setPrizes(prizes.filter(p => p.id !== prizeId))
    } catch (err) {
      console.error('Error deleting prize:', err)
      notify.error(err.message || 'Failed to delete prize')
    }
  }

  const handleSave = (savedPrize) => {
    if (editingPrize) {
      setPrizes(prizes.map(p => p.id === savedPrize.id ? { ...p, ...savedPrize } : p))
    } else {
      // Reload to get full prize data with bracket info
      loadPrizes()
    }
    setShowForm(false)
    setEditingPrize(null)
  }

  const handleAssignComplete = (updatedPrize) => {
    // Reload to get updated winner info
    loadPrizes()
    setAssigningPrize(null)
  }

  const getPlaceLabel = (place) => {
    switch (place) {
      case 1: return '1st'
      case 2: return '2nd'
      case 3: return '3rd'
      default: return `${place}th`
    }
  }

  const getPlaceIcon = (place) => {
    switch (place) {
      case 1: return <Trophy className="h-4 w-4 text-[var(--color-crown)]" />
      case 2: return <Award className="h-4 w-4 text-muted-foreground" />
      case 3: return <Award className="h-4 w-4 text-[var(--lemon-8)]" />
      default: return <Gift className="h-4 w-4 text-muted-foreground" />
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Tournaments with no prize the owner added default to Bragging Rights.
  const braggingTournaments = tournamentsWithoutPrize(brackets, prizes)

  // Group prizes by tournament, then by bracket
  const prizesByTournament = prizes.reduce((acc, prize) => {
    const tournamentId = prize.tournament_id
    if (!acc[tournamentId]) {
      acc[tournamentId] = {
        tournament: prize.tournament,
        brackets: {}
      }
    }
    
    const bracketKey = prize.bracket_id || 'tournament_level'
    if (!acc[tournamentId].brackets[bracketKey]) {
      acc[tournamentId].brackets[bracketKey] = {
        bracket: prize.bracket,
        prizes: []
      }
    }
    acc[tournamentId].brackets[bracketKey].prizes.push(prize)
    return acc
  }, {})

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Prizes</CardTitle>
          </div>
          {isOwner && !showForm && !assigningPrize && (
            <Button onClick={() => setShowForm(true)} size="sm" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Add Prize
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Create/Edit Form */}
        {(showForm || editingPrize) && (
          <div className="mb-6 p-4 border rounded-lg bg-muted/30">
            <h3 className="font-medium mb-4">
              {editingPrize ? 'Edit Prize' : 'Create New Prize'}
            </h3>
            <PrizeForm
              prize={editingPrize}
              brackets={brackets}
              groupId={groupId}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false)
                setEditingPrize(null)
              }}
            />
          </div>
        )}

        {/* Assign Winner Form */}
        {assigningPrize && (
          <div className="mb-6 p-4 border rounded-lg bg-muted/30">
            <h3 className="font-medium mb-4">
              Assign Winners for "{assigningPrize.name}"
            </h3>
            <AssignWinner
              prize={assigningPrize}
              groupId={groupId}
              onAssign={handleAssignComplete}
              onCancel={() => setAssigningPrize(null)}
            />
          </div>
        )}

        {/* Prizes List */}
        {prizes.length === 0 && braggingTournaments.length === 0 && !showForm ? (
          <div className="text-center py-8 text-muted-foreground">
            <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No prizes yet</p>
            {isOwner && (
              <p className="text-sm mt-1">Add prizes to reward your bracket winners!</p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {braggingTournaments.length > 0 && (
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-base">Default prize: Bragging Rights</h3>
                  {isOwner && (
                    <p className="text-sm text-muted-foreground">
                      These tournaments have no prize yet, so the group&apos;s top scorer wins bragging rights
                      and the dub Champ crown. Add a prize to replace it.
                    </p>
                  )}
                </div>
                <GroupBraggingRights groupId={groupId} tournaments={braggingTournaments} />
              </div>
            )}
            {Object.entries(prizesByTournament).map(([tournamentId, { tournament, brackets }]) => (
              <div key={tournamentId} className="space-y-4">
                <h3 className="font-semibold text-base">
                  {tournament?.name || 'Unknown Tournament'}
                </h3>
                {Object.entries(brackets).map(([bracketKey, { bracket, prizes: bracketPrizes }]) => (
                  <div key={bracketKey}>
                    <h4 className="font-medium text-sm text-muted-foreground mb-3 ml-4">
                      {bracketKey === 'tournament_level' ? <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4" /> Tournament-level prizes</span> : (bracket?.name || 'Unknown Bracket')}
                    </h4>
                <div className="space-y-3">
                  {bracketPrizes
                    .sort((a, b) => a.place - b.place)
                    .map((prize) => (
                      <div
                        key={prize.id}
                        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg border"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                            {getPlaceIcon(prize.place)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{prize.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {getPlaceLabel(prize.place)} Place
                              </Badge>
                            </div>
                            {prize.description && (
                              <p className="text-sm text-muted-foreground">{prize.description}</p>
                            )}
                            {hasPrizeWinner(prize) && (
                              <div className="flex flex-wrap items-center gap-1 mt-1 text-sm">
                                <User className="h-3 w-3" />
                                <span className="text-[var(--color-success)] font-medium">
                                  {getPrizeWinners(prize).length > 1 ? 'Tie: won by ' : 'Won by '}
                                  {formatWinnerNames(getPrizeWinners(prize))}
                                </span>
                                {getPrizeWinners(prize).every((w) => w.claimed_at ?? prize.claimed_at) && (
                                  <Badge variant="secondary" className="text-xs ml-2">Claimed</Badge>
                                )}
                              </div>
                            )}
                            {isOwner && prize.promo_code && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Code: <code className="bg-muted px-1 rounded">{prize.promo_code}</code>
                              </p>
                            )}
                          </div>
                        </div>

                        {isOwner && !showForm && !editingPrize && !assigningPrize && (
                          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
                            {!hasPrizeWinner(prize) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setAssigningPrize(prize)}
                              >
                                <Trophy className="h-4 w-4 mr-1" />
                                Assign Winners
                              </Button>
                            )}
                            {hasPrizeWinner(prize) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setAssigningPrize(prize)}
                              >
                                Change Winners
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingPrize(prize)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {!hasPrizeWinner(prize) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(prize.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
